// ============================================================
// 📁 آدرس این فایل در پروژه (فایل جدید):
//    src/utils/txnReport.ts
//
// موتور مشترک گزارش‌های ۷ تراکنش ژورنال (خرید، فروش، دریافت پول، پرداخت پول،
// ثبت مصارف، هزینه اجناس، انتقال پول) + گزارش جامع ژورنال.
// دو خروجی با ساختار و ظاهر یکسان:
//   • اکسل واقعی با فرمول‌های زنده‌ی SUM / AVERAGE / SUMIF / AVERAGEIF
//   • PDF (چاپ مرورگر → Save as PDF)
// جمع‌ها همیشه به تفکیک ارز هستند؛ هیچ ارزی با ارز دیگر جمع نمی‌شود.
// ============================================================

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { gregorianToJalaliStr as _g2j, todayGregorian } from "./PersianDatePicker";

declare const process: any;

// ───────── ابزارها ─────────
const toEn = (s: string) => String(s ?? "").replace(/[۰-۹]/g, d => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))).replace(/[٠-٩]/g, d => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
export const jal = (d: string) => (d ? toEn(_g2j(d)) : "");
export const nowJalali = () => jal(todayGregorian());
export const nowTime = () => new Date().toLocaleTimeString("en-GB");
export const km = (x: any) => String(x ?? "").replace(/کمیسیون/g, "کمیشن");
export const esc = (s: any) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const num = (v: any) => { const n = Number(v); return isFinite(n) ? n : 0; };
export const nf = (n: number, d = 2) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d }).format(n);
export const nf2 = (n: number) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export const fileUrl = (path: string) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  let origin = window.location.origin;
  try { if (process.env.REACT_APP_API_URL) origin = new URL(process.env.REACT_APP_API_URL).origin; } catch {}
  return origin + (path.startsWith("/") ? path : "/" + path);
};

const L2 = (n: number) => { let s = ""; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };

// ───────── مدل گزارش ─────────
export type ColKind = "text" | "wrap" | "int" | "qty" | "money" | "rate" | "date" | "link" | "badge";

export interface RCol {
  key: string;
  header: string;
  width: number;
  kind?: ColKind;
  // اگر ستون جمع‌پذیر است: cur = نام ستونِ حاوی ارز همان ردیف  |  cur = "=USD" (ارز ثابت)  |  cur = "" (بدون ارز، مثل تعداد)
  sum?: { cur: string };
  showSum?: boolean;   // پیش‌فرض true
  showAvg?: boolean;   // پیش‌فرض false (فقط برای قیمت واحد)
  align?: "right" | "center" | "left";
}

export interface RCard { label: string; key: string; cur?: string } // cur: ارز مشخص یا خالی = همه‌ی ارزها

export interface RSpec {
  type: string;
  title: string;
  subtitle: string;
  icon: string;
  color: string;       // رنگ اصلی (hex بدون #)
  cols: RCol[];
  rows: Record<string, any>[];
  cards: RCard[];
  docCount?: number;   // تعداد سند (برای کارت اول)
  extra?: { title: string; cols: RCol[]; rows: Record<string, any>[]; note?: string }[]; // جدول‌های جانبی (مثل مفاد و ضرر تبادله)
  note?: string;
  extraCards?: { label: string; value: number }[]; // کارت‌های ثابت (بدون ستون)
}

export interface ReportMeta { periodLabel: string; filtersLabel: string; }

// ───────── ساخت ردیف‌ها از پاسخ سرور ─────────
const stLabel = (s: string) => (s === "pending" ? "در انتظار" : s === "confirmed" || s === "paid" ? "نهایی" : s || "");
const commaJoin = (a: any, fallback = "") => (Array.isArray(a) && a.length ? a.filter((x: any) => String(x ?? "").trim() !== "").join(", ") : fallback);

const COGS_TYPE_LABEL: Record<string, string> = {
  product: "محصول", fare_domestic: "کرایه موتر داخلی", fare_foreign: "کرایه موتر خارجی", customs_commission: "کمیشن گمرک",
  waybill: "بارنامه", special_unload: "تخلیه ویژه", health_inquiry: "استعلام صحت عامه", labor: "حمالی", other: "سایر مصارف",
};

const cols = {
  idx: { key: "idx", header: "#", width: 6, kind: "int" as ColKind },
  serial: { key: "serial", header: "شماره تراکنش", width: 15, kind: "badge" as ColKind },
  date: { key: "date", header: "تاریخ", width: 12, kind: "date" as ColKind },
  status: { key: "status", header: "وضعیت", width: 10, kind: "text" as ColKind },
  notes: { key: "notes", header: "توضیحات", width: 34, kind: "wrap" as ColKind, align: "right" as const },
  att: { key: "att", header: "ضمیمه", width: 10, kind: "link" as ColKind },
};

export function buildSpec(type: string, data: any[], opt?: { fx?: any[] }): RSpec {
  const rows: Record<string, any>[] = [];
  let spec: RSpec;

  if (type === "purchases" || type === "sales") {
    const isP = type === "purchases";
    let g = 0;
    data.forEach(d => {
      g++;
      const items = (d.items && d.items.length) ? d.items : [null];
      items.forEach((it: any, k: number) => {
        rows.push({
          _g: g, serial: d.serial, date: d.date,
          kind: isP ? (d.purchase_type === "foreign" ? "خارجی" : "داخلی") : (d.sale_type === "foreign" ? "خارجی" : "داخلی"),
          party: isP ? d.vendor_name : d.customer_name,
          cur: isP ? d.vendor_currency : d.customer_currency,
          item: it?.product_name || "", code: it?.product_code || "",
          warehouse: it?.warehouse_name || "",
          unit: it?.unit || "", qty: it ? num(it.quantity) : null,
          base_unit: it?.base_unit_name || "", base_qty: it && it.base_quantity != null ? num(it.base_quantity) : null,
          price: it ? num(it.unit_price) : null, line: it ? num(it.total_price) : null,
          discount: k === 0 ? num(d.discount) : null, net: k === 0 ? num(d.net_amount) : null,
          truck: d.truck_plate || "", waybill: d.waybill_number || "",
          status: stLabel(d.status), notes: d.notes || "", att: d.attachment || "",
        });
      });
    });
    const c: RCol[] = [
      cols.idx, cols.serial, cols.date,
      { key: "kind", header: isP ? "نوع خرید" : "نوع فروش", width: 10, kind: "text" },
      { key: "party", header: isP ? "نام فروشنده" : "نام مشتری", width: 24, kind: "text", align: "right" },
      { key: "cur", header: "ارز", width: 8, kind: "text" },
      { key: "item", header: "نام جنس", width: 24, kind: "text", align: "right" },
      { key: "code", header: "کود جنس", width: 12, kind: "text" },
      ...(isP ? [] : [{ key: "warehouse", header: "گدام", width: 16, kind: "text" as ColKind }]),
      { key: "unit", header: "واحد شمارش", width: 11, kind: "text" },
      { key: "qty", header: "تعداد", width: 12, kind: "qty", sum: { cur: "" } },
      { key: "base_unit", header: "واحد پایه", width: 11, kind: "text" },
      { key: "base_qty", header: "مقدار به واحد پایه", width: 14, kind: "qty" },
      { key: "price", header: "قیمت واحد", width: 14, kind: "money", sum: { cur: "cur" }, showSum: false, showAvg: true },
      { key: "line", header: isP ? "مبلغ خرید جنس" : "مبلغ فروش جنس", width: 16, kind: "money", sum: { cur: "cur" } },
      { key: "discount", header: "تخفیف سند", width: 12, kind: "money", sum: { cur: "cur" } },
      { key: "net", header: isP ? "خالص کل خرید (سند)" : "خالص کل فروش (سند)", width: 18, kind: "money", sum: { cur: "cur" } },
      ...(isP ? [] : [{ key: "truck", header: "شماره موتر", width: 13, kind: "text" as ColKind }, { key: "waybill", header: "شماره بارنامه", width: 13, kind: "text" as ColKind }]),
      cols.status, cols.notes, cols.att,
    ];
    spec = {
      type, title: isP ? "گزارش خریدها" : "گزارش فروش‌ها", subtitle: isP ? "جزئیات کامل اجناس، واحد، مقدار، قیمت و مبلغ هر خرید" : "جزئیات کامل اجناس، گدام، واحد، مقدار، قیمت و مبلغ هر فروش",
      icon: isP ? "🛒" : "💰", color: isP ? "D97706" : "059669", cols: c, rows,
      cards: [{ label: isP ? "جمع مبلغ خرید" : "جمع مبلغ فروش", key: "line" }, { label: "جمع تخفیف", key: "discount" }, { label: "جمع خالص", key: "net" }],
      docCount: data.length,
    };
  } else if (type === "payments_in" || type === "payments_out") {
    const isIn = type === "payments_in";
    data.forEach(d => {
      const rc = isIn ? d.receipt_currency : d.payment_currency;
      const pc = isIn ? d.customer_currency : d.vendor_currency;
      const vendorSide = isIn ? !!(d.vendor_id && !d.customer_id) : !(d.customer_id && !d.vendor_id);
      rows.push({
        serial: d.serial, date: d.date,
        party: isIn ? d.customer_name : d.vendor_name,
        party_kind: isIn ? (vendorSide ? "فروشنده" : "مشتری") : (vendorSide ? "فروشنده" : "مشتری"),
        cur_paid: rc, amt_paid: num(isIn ? d.receipt_amount : d.payment_amount),
        rate: num(d.exchange_rate), cur_party: pc, amt_party: num(isIn ? d.customer_amount : d.vendor_amount),
        account: d.account_name || "", ref: d.reference_number || "",
        status: stLabel(d.status), notes: d.notes || "", att: d.attachment || "",
      });
    });
    const c: RCol[] = [
      cols.idx, cols.serial, cols.date,
      { key: "party", header: isIn ? "نام پرداخت‌کننده" : "نام دریافت‌کننده", width: 24, kind: "text", align: "right" },
      { key: "party_kind", header: "نوع طرف حساب", width: 11, kind: "text" },
      { key: "cur_paid", header: isIn ? "ارز دریافتی" : "ارز پرداختی", width: 11, kind: "text" },
      { key: "amt_paid", header: isIn ? "مبلغ دریافتی" : "مبلغ پرداختی", width: 16, kind: "money", sum: { cur: "cur_paid" } },
      { key: "rate", header: "نرخ تبدیل", width: 12, kind: "rate" },
      { key: "cur_party", header: "ارز طرف حساب", width: 11, kind: "text" },
      { key: "amt_party", header: "مبلغ به ارز طرف حساب", width: 18, kind: "money", sum: { cur: "cur_party" } },
      { key: "account", header: isIn ? "حساب دریافت" : "حساب پرداخت", width: 24, kind: "text", align: "right" },
      { key: "ref", header: "شماره مرجع", width: 14, kind: "text" },
      cols.status, cols.notes, cols.att,
    ];
    spec = {
      type, title: isIn ? "گزارش دریافت پول" : "گزارش پرداخت پول", subtitle: "ارز و مبلغ واقعی، نرخ تبدیل، مبلغ به ارز طرف حساب و حساب مربوطه",
      icon: isIn ? "📥" : "📤", color: isIn ? "2563EB" : "DC2626", cols: c, rows,
      cards: [{ label: isIn ? "جمع دریافتی" : "جمع پرداختی", key: "amt_paid" }, { label: "جمع به ارز طرف حساب", key: "amt_party" }],
      docCount: data.length,
    };
  } else if (type === "expenses") {
    data.forEach(d => {
      rows.push({
        serial: d.serial, date: d.date, category: km(d.category_name), recipient: d.recipient_name || "",
        cur: d.currency, amount: num(d.amount), rate: num(d.exchange_rate), usd: num(d.usd_amount),
        account: d.account_name || "",
        status: d.status === "pending" ? "در انتظار پرداخت" : "پرداخت شده", notes: d.notes || "", att: d.attachment || "",
      });
    });
    const c: RCol[] = [
      cols.idx, cols.serial, cols.date,
      { key: "category", header: "دسته‌بندی مصرف", width: 22, kind: "text", align: "right" },
      { key: "recipient", header: "گیرنده / بابت", width: 20, kind: "text", align: "right" },
      { key: "cur", header: "ارز مصرف", width: 10, kind: "text" },
      { key: "amount", header: "مبلغ مصرف", width: 16, kind: "money", sum: { cur: "cur" } },
      { key: "rate", header: "نرخ تبدیل به دالر", width: 14, kind: "rate" },
      { key: "usd", header: "معادل دالر", width: 16, kind: "money", sum: { cur: "=USD" } },
      { key: "account", header: "حساب پرداخت‌کننده", width: 24, kind: "text", align: "right" },
      cols.status, cols.notes, cols.att,
    ];
    spec = {
      type, title: "گزارش ثبت مصارف", subtitle: "مصارف به ارز مصرف، نرخ تبدیل، معادل دالر و حساب پرداخت‌کننده",
      icon: "🧾", color: "7C3AED", cols: c, rows,
      cards: [{ label: "جمع مصارف", key: "amount" }, { label: "جمع معادل دالر", key: "usd", cur: "USD" }], docCount: data.length,
    };
  } else if (type === "cogs") {
    data.forEach(d => {
      const t = d.cogs_type || "";
      rows.push({
        serial: d.serial, date: d.date, ctype: t,
        category: km(d.category_name || COGS_TYPE_LABEL[t] || ""),
        title: d.cogs_title || "",
        item: d.item_name || "", code: d.item_code || "",
        truck_count: d.truck_count != null && d.truck_count !== "" ? num(d.truck_count) : null,
        trucks: commaJoin(d.truck_numbers_list, d.truck_plate || ""),
        waybills: commaJoin(d.waybill_numbers_list, d.waybill_number || ""),
        inumbers: commaJoin(d.inumbers_list),
        from: d.route_from || "", to: d.route_to || "",
        f_base: d.fare_base != null ? num(d.fare_base) : null, f_stop: d.fare_stop != null ? num(d.fare_stop) : null,
        f_toll: d.fare_toll != null ? num(d.fare_toll) : null, f_scale: d.fare_scale != null ? num(d.fare_scale) : null,
        f_city: d.fare_city != null ? num(d.fare_city) : null, f_other: d.fare_other != null ? num(d.fare_other) : null,
        cur: d.currency, amount: num(d.amount), rate: num(d.exchange_rate), usd: num(d.usd_amount),
        account: d.account_name || "",
        status: d.status === "pending" ? "در انتظار پرداخت" : "پرداخت شده",
        notes: d.notes || "", notes2: d.notes2 || "", att: d.attachment || "",
      });
    });
    const money = (key: string, header: string): RCol => ({ key, header, width: 13, kind: "money", sum: { cur: "cur" } });
    const c: RCol[] = [
      cols.idx, cols.serial, cols.date,
      { key: "category", header: "دسته‌بندی", width: 20, kind: "text", align: "right" },
      { key: "title", header: "عنوان", width: 16, kind: "text", align: "right" },
      { key: "item", header: "جنس مربوطه", width: 20, kind: "text", align: "right" },
      { key: "code", header: "کود جنس", width: 11, kind: "text" },
      { key: "truck_count", header: "تعداد موتر", width: 9, kind: "int", sum: { cur: "" }, showSum: false },
      { key: "trucks", header: "شماره موترها", width: 24, kind: "wrap", align: "right" },
      { key: "waybills", header: "شماره بارنامه‌ها", width: 24, kind: "wrap", align: "right" },
      { key: "inumbers", header: "آی نمبرها", width: 24, kind: "wrap", align: "right" },
      { key: "from", header: "مبدا", width: 14, kind: "text" },
      { key: "to", header: "مقصد", width: 14, kind: "text" },
      money("f_base", "اصل کرایه"), money("f_stop", "توقفی"), money("f_toll", "تول پلازا / ۵٪ / ۲٪"),
      money("f_scale", "باسکول"), money("f_city", "کرایه شهری"), money("f_other", "سایر هزینه‌ها"),
      { key: "cur", header: "ارز مصرف", width: 10, kind: "text" },
      { key: "amount", header: "مبلغ مصرف", width: 16, kind: "money", sum: { cur: "cur" } },
      { key: "rate", header: "نرخ تبدیل", width: 12, kind: "rate" },
      { key: "usd", header: "معادل دالر", width: 16, kind: "money", sum: { cur: "=USD" } },
      { key: "account", header: "حساب پرداخت", width: 22, kind: "text", align: "right" },
      cols.status,
      { key: "notes", header: "توضیحات", width: 40, kind: "wrap", align: "right" },
      { key: "notes2", header: "توضیحات ۲", width: 30, kind: "wrap", align: "right" },
      cols.att,
    ];
    spec = {
      type, title: "گزارش هزینه اجناس (COGS)", subtitle: "دسته‌بندی، جنس مربوطه، موترها و بارنامه‌ها، جزئیات کرایه، نرخ و معادل دالر",
      icon: "📦", color: "0F766E", cols: c, rows,
      cards: [{ label: "جمع مصارف", key: "amount" }, { label: "جمع معادل دالر", key: "usd", cur: "USD" }],
      docCount: data.length,
      extraCards: (() => {
        const tc = (ty: string) => rows.filter(r => r.ctype === ty).reduce((s, r) => s + (r.truck_count ? num(r.truck_count) : 0), 0);
        const p = tc("product"), fd = tc("fare_domestic"), ff = tc("fare_foreign");
        return [
          { label: "🚚 تعداد موتر محصول", value: p },
          { label: "🚛 تعداد موتر کرایه داخلی", value: fd },
          { label: "🚛 تعداد موتر کرایه خارجی", value: ff },
          { label: "🚛 جمع موتر کرایه (داخلی + خارجی)", value: fd + ff },
        ];
      })(),
    };
  } else {
    // transfers
    data.forEach(d => {
      rows.push({
        serial: d.serial, date: d.date,
        from_acc: d.from_account_name || "", from_cur: d.from_currency || "", to_acc: d.to_account_name || "", to_cur: d.to_currency || "",
        from_amt: num(d.from_amount), rate: num(d.exchange_rate), to_amt: num(d.to_amount),
        fx: d.fx_diff != null ? num(d.fx_diff) : 0, fx_cur: d.fx_currency || d.to_currency || "",
        ref: d.reference_number || "", status: stLabel(d.status), notes: d.notes || "", att: d.attachment || "",
      });
    });
    const c: RCol[] = [
      cols.idx, cols.serial, cols.date,
      { key: "from_acc", header: "حساب مبدا", width: 24, kind: "text", align: "right" },
      { key: "from_cur", header: "ارز حساب مبدا", width: 11, kind: "text" },
      { key: "to_acc", header: "حساب مقصد", width: 24, kind: "text", align: "right" },
      { key: "to_cur", header: "ارز حساب مقصد", width: 11, kind: "text" },
      { key: "from_amt", header: "مبلغ مبدا", width: 16, kind: "money", sum: { cur: "from_cur" } },
      { key: "rate", header: "نرخ تبدیل", width: 12, kind: "rate" },
      { key: "to_amt", header: "مبلغ مقصد", width: 16, kind: "money", sum: { cur: "to_cur" } },
      { key: "fx", header: "مفاد و ضرر تبادله", width: 16, kind: "money", sum: { cur: "fx_cur" } },
      { key: "fx_cur", header: "ارز تفاوت", width: 9, kind: "text" },
      { key: "ref", header: "شماره مرجع", width: 13, kind: "text" },
      cols.status, cols.notes, cols.att,
    ];
    const fx = opt?.fx || [];
    spec = {
      type, title: "گزارش انتقال پول", subtitle: "انتقال بین حساب‌ها — مبلغ مبدا، مبلغ مقصد و نرخ",
      icon: "🔁", color: "0EA5E9", cols: c, rows,
      cards: [{ label: "جمع مبالغ مبدا", key: "from_amt" }, { label: "جمع مبالغ مقصد", key: "to_amt" }, { label: "مفاد و ضرر تبادله", key: "fx" }],
      docCount: data.length,
      extra: [{
        title: "مفاد و ضرر تبادله — به تفکیک ارز",
        cols: [
          { key: "currency", header: "ارز", width: 10, kind: "text" },
          { key: "count", header: "تعداد انتقال", width: 12, kind: "int" },
          { key: "gain", header: "مفاد", width: 16, kind: "money" },
          { key: "loss", header: "ضرر", width: 16, kind: "money" },
          { key: "net", header: "خالص مفاد (ضرر)", width: 18, kind: "money" },
        ],
        rows: fx.map((x: any) => ({ currency: x.currency, count: x.count, gain: num(x.gain), loss: num(x.loss), net: num(x.net) })),
      }],
    };
  }
  rows.forEach((r, i) => { r.idx = i + 1; });
  return spec;
}

// ───────── محاسبه‌ی جمع‌ها (برای PDF و مقدار اولیه‌ی فرمول‌های اکسل) ─────────
export function currenciesOf(spec: RSpec): string[] {
  const set = new Set<string>();
  spec.cols.forEach(c => {
    if (!c.sum) return;
    if (c.sum.cur.startsWith("=")) set.add(c.sum.cur.slice(1));
    else if (c.sum.cur) spec.rows.forEach(r => { const v = r[c.sum!.cur]; if (v && r[c.key] !== null && r[c.key] !== undefined && r[c.key] !== "") set.add(String(v)); });
  });
  const arr = Array.from(set);
  arr.sort((a, b) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)));
  return arr;
}
const hasPlainSum = (spec: RSpec) => spec.cols.some(c => c.sum && c.sum.cur === "");

export function stat(spec: RSpec, c: RCol, cur: string | null): { sum: number; avg: number; n: number } | null {
  if (!c.sum) return null;
  let list = spec.rows.filter(r => r[c.key] !== null && r[c.key] !== undefined && r[c.key] !== "");
  if (cur === null) { if (c.sum.cur !== "") return null; }
  else {
    if (c.sum.cur === "") return null;
    if (c.sum.cur.startsWith("=")) { if (c.sum.cur.slice(1) !== cur) return null; }
    else list = list.filter(r => String(r[c.sum!.cur]) === cur);
    if (!list.length) return null;
  }
  const sum = list.reduce((s, r) => s + num(r[c.key]), 0);
  return { sum, avg: list.length ? sum / list.length : 0, n: list.length };
}

export interface CardVal { label: string; value: number; cur: string; fmt: string; key: string }
export function cardValues(spec: RSpec): CardVal[] {
  const out: CardVal[] = [];
  const curs = currenciesOf(spec);
  spec.cards.forEach(cd => {
    const col = spec.cols.find(c => c.key === cd.key)!;
    if (col.sum!.cur === "") {
      const s = stat(spec, col, null); out.push({ label: cd.label, value: s ? s.sum : 0, cur: "", fmt: "#,##0.##", key: cd.key });
    } else {
      const list = cd.cur ? [cd.cur] : curs;
      list.forEach(cu => { const s = stat(spec, col, cu); if (s) out.push({ label: `${cd.label} · ${cu}`, value: s.sum, cur: cu, fmt: "#,##0.00", key: cd.key }); });
    }
  });
  return out;
}

// ───────── Excel ─────────
const fillOf = (argb: string) => ({ type: "pattern" as const, pattern: "solid" as const, fgColor: { argb } });
const NAVY = "1E293B", WHITE = "FFFFFF", LIGHT = "F1F5F9", BLUE = "2563EB", GREEN = "059669", RED = "DC2626";
const CARD_COLORS = ["7C3AED", "2563EB", "059669", "D97706", "DC2626", "0EA5E9", "0F766E", "9A3412"];
const numFmtOf = (k?: ColKind) => k === "money" ? "#,##0.00" : k === "qty" ? "#,##0.####" : k === "rate" ? "#,##0.00####" : k === "int" ? "#,##0" : undefined;

function rowHeightFor(spec: RSpec, r: Record<string, any>): number {
  let lines = 1;
  spec.cols.forEach(c => {
    if (c.kind !== "wrap") return;
    const v = String(r[c.key] ?? "");
    if (!v) return;
    const per = Math.max(8, Math.round(c.width * 1.35 * 0.85));
    lines = Math.max(lines, Math.ceil(v.length / per));
  });
  return Math.max(26, lines * 20);
}

// یک شیت کامل برای spec می‌سازد (در workbook داده‌شده)
export function addSpecSheet(wb: ExcelJS.Workbook, spec: RSpec, meta: ReportMeta, sheetName?: string) {
  const ws = wb.addWorksheet((sheetName || spec.title).replace(/[\\/?*[\]:]/g, " ").slice(0, 31), {
    views: [{ rightToLeft: true, showGridLines: false }],
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0, paperSize: 8 as any },
  });
  const N = spec.cols.length;
  const ACCENT = spec.color;
  ws.columns = spec.cols.map(c => ({ key: c.key, width: Math.round(c.width * 1.35) }));
  const Lc = (k: string) => ws.getColumn(k).letter;

  // عنوان
  ws.mergeCells(1, 1, 2, N);
  const t = ws.getCell("A1");
  t.value = `${spec.icon}  ${spec.title}`;
  t.font = { name: "Arial", size: 24, bold: true, color: { argb: WHITE } };
  t.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(1).height = 26; ws.getRow(2).height = 26;
  for (let r = 1; r <= 2; r++) for (let c = 1; c <= N; c++) ws.getCell(r, c).fill = fillOf(NAVY);
  ws.mergeCells(3, 1, 3, N);
  const sub = ws.getCell("A3");
  sub.value = `دوره: ${meta.periodLabel}   |   فیلترها: ${meta.filtersLabel || "بدون فیلتر"}   |   تاریخ تولید گزارش: ${nowJalali()}   ${nowTime()}`;
  sub.font = { name: "Arial", size: 14, color: { argb: WHITE } };
  sub.alignment = { horizontal: "center" };
  sub.fill = fillOf(ACCENT);
  ws.getRow(3).height = 20; ws.getRow(4).height = 8;

  // موقعیت جدول
  const cards = cardValues(spec);
  const cardList: CardVal[] = [{ label: "🔢 تعداد سند", value: spec.docCount ?? spec.rows.length, cur: "", fmt: "#,##0", key: "__docs" }, ...(spec.extraCards || []).map(c => ({ label: c.label, value: c.value, cur: "", fmt: "#,##0", key: "__docs" })), ...cards];
  const perRow = Math.min(6, Math.max(3, Math.min(N, 6)));
  const cardRows = Math.ceil(cardList.length / perRow);
  const tableTitleRow = 5 + cardRows * 3;
  const headerRow = tableTitleRow + 2;
  const first = headerRow + 1;
  const last = first + Math.max(spec.rows.length, 1) - 1;
  const rng = (k: string) => `${Lc(k)}${first}:${Lc(k)}${last}`;

  // کارت‌های خلاصه (فرمول زنده)
  cardList.forEach((cd, i) => {
    const rowBlock = Math.floor(i / perRow), idxIn = i % perRow;
    const inThis = Math.min(perRow, cardList.length - rowBlock * perRow);
    const span = Math.max(1, Math.floor(N / inThis));
    const s = 1 + idxIn * span, e = idxIn === inThis - 1 ? N : s + span - 1;
    const rLabel = 5 + rowBlock * 3, rVal = rLabel + 1;
    const color = CARD_COLORS[i % CARD_COLORS.length];
    if (e > s) { ws.mergeCells(rLabel, s, rLabel, e); ws.mergeCells(rVal, s, rVal, e); }
    const lc = ws.getCell(rLabel, s); lc.value = cd.label;
    lc.font = { name: "Arial", size: 13, bold: true, color: { argb: "64748B" } };
    lc.alignment = { horizontal: "center", vertical: "middle" };
    const vc = ws.getCell(rVal, s);
    if (cd.key === "__docs") {
      vc.value = cd.value;
    } else {
      const col = spec.cols.find(c => c.key === cd.key)!;
      if (col.sum!.cur === "" || col.sum!.cur.startsWith("=")) vc.value = { formula: `SUM(${rng(col.key)})`, result: cd.value } as any;
      else vc.value = { formula: `SUMIF(${rng(col.sum!.cur)},"${cd.cur}",${rng(col.key)})`, result: cd.value } as any;
    }
    vc.numFmt = cd.fmt;
    vc.font = { name: "Arial", size: 17, bold: true, color: { argb: color } };
    vc.alignment = { horizontal: "center", vertical: "middle" };
    for (let r = rLabel; r <= rVal; r++) for (let c = s; c <= e; c++) {
      ws.getCell(r, c).fill = fillOf(LIGHT);
      ws.getCell(r, c).border = {
        top: { style: "thin", color: { argb: "E2E8F0" } }, bottom: { style: "thin", color: { argb: "E2E8F0" } },
        left: { style: c === s ? "medium" : "thin", color: { argb: c === s ? color : "E2E8F0" } },
        right: { style: "thin", color: { argb: "E2E8F0" } },
      };
    }
    ws.getRow(rLabel).height = 18; ws.getRow(rVal).height = 24; ws.getRow(rVal + 1).height = 6;
  });

  // عنوان جدول
  ws.mergeCells(tableTitleRow, 1, tableTitleRow, N);
  const tt = ws.getCell(tableTitleRow, 1);
  tt.value = "📋  جزئیات کامل";
  tt.font = { name: "Arial", size: 15.5, bold: true, color: { argb: NAVY } };
  tt.border = { bottom: { style: "medium", color: { argb: ACCENT } } };
  ws.getRow(tableTitleRow).height = 20; ws.getRow(tableTitleRow + 1).height = 4;

  // هدر
  const hr = ws.getRow(headerRow);
  spec.cols.forEach((c, i) => {
    const cell = hr.getCell(i + 1);
    cell.value = c.header;
    cell.font = { name: "Arial", size: 13.5, bold: true, color: { argb: WHITE } };
    cell.fill = fillOf(NAVY);
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = { top: { style: "thin" }, bottom: { style: "medium", color: { argb: ACCENT } }, left: { style: "thin", color: { argb: "334155" } }, right: { style: "thin", color: { argb: "334155" } } };
  });
  hr.height = 40;
  ws.views = [{ rightToLeft: true, showGridLines: false, state: "frozen", ySplit: headerRow }];

  // ردیف‌ها
  if (spec.rows.length === 0) {
    ws.mergeCells(first, 1, first, N);
    ws.getCell(first, 1).value = "هیچ ردیفی یافت نشد";
    ws.getCell(first, 1).alignment = { horizontal: "center" };
  }
  let lastG = -1, shade = false;
  spec.rows.forEach((r, i) => {
    const row = ws.getRow(first + i);
    row.height = rowHeightFor(spec, r);
    if (r._g !== undefined) { if (r._g !== lastG) { shade = !shade; lastG = r._g; } } else shade = i % 2 === 0;
    spec.cols.forEach((c, ci) => {
      const cell = row.getCell(ci + 1);
      let v = r[c.key];
      if (c.kind === "date") v = v ? jal(v) : "";
      if (c.kind === "link") { if (v) cell.value = { text: "مشاهده", hyperlink: fileUrl(String(v)) } as any; }
      else if (v === null || v === undefined || v === "") { /* خالی بماند تا در میانگین حساب نشود */ }
      else cell.value = v;
      const nfm = numFmtOf(c.kind); if (nfm) cell.numFmt = nfm;
      cell.fill = fillOf(shade ? WHITE : LIGHT);
      cell.font = { name: "Arial", size: 13, color: { argb: "1E293B" } };
      cell.border = { top: { style: "thin", color: { argb: "CBD5E1" } }, bottom: { style: "thin", color: { argb: "CBD5E1" } }, left: { style: "thin", color: { argb: "CBD5E1" } }, right: { style: "thin", color: { argb: "CBD5E1" } } };
      cell.alignment = { vertical: "middle", horizontal: c.align || "center", wrapText: c.kind === "wrap" };
      if (c.kind === "badge") cell.font = { name: "Arial", size: 13, bold: true, color: { argb: ACCENT } };
      if (c.kind === "link" && v) cell.font = { name: "Arial", size: 13, bold: true, color: { argb: BLUE }, underline: true };
      if (c.key === "status") cell.font = { name: "Arial", size: 13, bold: true, color: { argb: String(v).startsWith("در انتظار") ? "D97706" : GREEN } };
      if (c.key === "fx" && typeof v === "number" && v !== 0) cell.font = { name: "Arial", size: 13, bold: true, color: { argb: v < 0 ? RED : GREEN } };
    });
  });

  // جمع‌ها و میانگین‌ها (فرمول واقعی؛ به تفکیک ارز)
  let nextRow = last + 1;
  if (spec.rows.length > 0) {
    const curs = currenciesOf(spec);
    const blocks: { label: string; cur: string | null }[] = [];
    if (hasPlainSum(spec)) blocks.push({ label: "", cur: null });
    curs.forEach(cu => blocks.push({ label: cu, cur: cu }));
    const firstSumCol = Math.max(1, spec.cols.findIndex(c => !!c.sum));
    blocks.forEach(b => {
      (["SUM", "AVERAGE"] as const).forEach(fn => {
        const use = spec.cols.filter(c => { const st = stat(spec, c, b.cur); return !!st && (fn === "SUM" ? c.showSum !== false : !!c.showAvg); });
        if (use.length === 0) return;
        const rowN = nextRow++;
        const row = ws.getRow(rowN);
        const firstUse = Math.max(1, spec.cols.indexOf(use[0]));
        if (firstUse > 1) ws.mergeCells(rowN, 1, rowN, firstUse);
        row.getCell(1).value = (fn === "SUM" ? "جمع کل" : "میانگین قیمت") + (b.cur ? ` · ${b.label}` : "");
        use.forEach(c => {
          const ci = spec.cols.indexOf(c);
          const st = stat(spec, c, b.cur)!;
          const cell = row.getCell(ci + 1);
          let formula: string;
          if (b.cur === null || c.sum!.cur.startsWith("=")) formula = `${fn}(${rng(c.key)})`;
          else formula = fn === "SUM" ? `SUMIF(${rng(c.sum!.cur)},"${b.cur}",${rng(c.key)})` : `AVERAGEIF(${rng(c.sum!.cur)},"${b.cur}",${rng(c.key)})`;
          cell.value = { formula, result: fn === "SUM" ? st.sum : st.avg } as any;
          cell.numFmt = numFmtOf(c.kind) || "#,##0.00";
        });
        for (let c = 1; c <= N; c++) {
          const cell = row.getCell(c);
          cell.font = { name: "Arial", size: 14, bold: true, color: { argb: NAVY } };
          cell.fill = fillOf(fn === "SUM" ? "FEF3C7" : "E0F2FE");
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = fn === "SUM" ? { top: { style: "double", color: { argb: NAVY } } } : {};
        }
        row.height = 28;
      });
    });
  }

  // جدول‌های جانبی
  (spec.extra || []).forEach(ex => {
    nextRow += 1;
    ws.mergeCells(nextRow, 1, nextRow, N);
    const h = ws.getCell(nextRow, 1);
    h.value = "📊  " + ex.title;
    h.font = { name: "Arial", size: 15, bold: true, color: { argb: NAVY } };
    h.border = { bottom: { style: "medium", color: { argb: ACCENT } } };
    nextRow += 1;
    const hr2 = ws.getRow(nextRow);
    ex.cols.forEach((c, i) => {
      const cell = hr2.getCell(i + 1); cell.value = c.header;
      cell.font = { name: "Arial", size: 13.5, bold: true, color: { argb: WHITE } }; cell.fill = fillOf(NAVY);
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });
    hr2.height = 22;
    const fr = nextRow + 1;
    ex.rows.forEach((r, i) => {
      nextRow += 1;
      const row = ws.getRow(nextRow);
      ex.cols.forEach((c, ci) => {
        const cell = row.getCell(ci + 1); cell.value = r[c.key];
        const nfm = numFmtOf(c.kind); if (nfm) cell.numFmt = nfm;
        cell.alignment = { horizontal: "center" };
        cell.font = { name: "Arial", size: 13, color: { argb: "1E293B" } };
        cell.fill = fillOf(i % 2 === 0 ? WHITE : LIGHT);
      });
    });
    if (ex.rows.length) {
      nextRow += 1;
      const row = ws.getRow(nextRow);
      row.getCell(1).value = "جمع";
      ex.cols.forEach((c, ci) => {
        if (c.kind !== "money" && c.key !== "count") return;
        const lt = L2(ci + 1);
        row.getCell(ci + 1).value = { formula: `SUM(${lt}${fr}:${lt}${nextRow - 1})`, result: ex.rows.reduce((s, r) => s + num(r[c.key]), 0) } as any;
        row.getCell(ci + 1).numFmt = numFmtOf(c.kind) || "#,##0";
      });
      for (let c = 1; c <= ex.cols.length; c++) {
        const cell = row.getCell(c);
        cell.font = { name: "Arial", size: 13.5, bold: true, color: { argb: NAVY } }; cell.fill = fillOf("FEF3C7"); cell.alignment = { horizontal: "center" };
      }
    }
  });

  return ws;
}

export async function exportSpecExcel(spec: RSpec, meta: ReportMeta) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "سیستم حسابداری"; wb.created = new Date();
  addSpecSheet(wb, spec, meta);
  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: "application/octet-stream" }), `${spec.title.replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ───────── PDF ─────────
export function pdfStyles(color: string, fs: number) {
  return `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
@page{size:A4 landscape;margin:8mm 8mm}
body{font-family:'Vazirmatn',Tahoma,sans-serif;direction:rtl;font-size:${fs + 2}px;color:#0f172a;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{padding:2px}
.header{display:flex;justify-content:space-between;align-items:center;padding:18px 24px;margin-bottom:16px;border-radius:14px;background:#${color};color:#fff}
.hr{display:flex;align-items:center;gap:14px}
.logo{width:52px;height:52px;background:rgba(255,255,255,.2);border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:26px}
.ht{font-size:26px;font-weight:800}.hs{font-size:13px;margin-top:4px;opacity:.92}
.hl{text-align:left}
.badge{display:inline-block;background:#fff;color:#${color};font-size:14px;font-weight:800;padding:5px 16px;border-radius:20px;margin-bottom:6px}
.pd{font-size:12.5px;opacity:.95}
.cards{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.card{flex:1;min-width:150px;border-radius:12px;padding:11px 15px;background:#f8fafc;border:1px solid #e2e8f0;border-right:6px solid var(--c)}
.cl{font-size:13px;font-weight:700;color:#475569;margin-bottom:4px}.cv{font-size:21px;font-weight:800;color:var(--c);direction:ltr;text-align:right}
.tt{font-size:18px;font-weight:800;color:#0f172a;margin:16px 0 9px;padding:6px 12px;border-right:5px solid #${color};background:#f1f5f9;border-radius:6px}
.tw{border-radius:10px;overflow:hidden;border:1.5px solid #cbd5e1}
table{width:100%;border-collapse:collapse;font-size:${fs}px}
thead tr{background:#1e293b}
th{padding:10px 6px;text-align:center;font-weight:800;color:#fff;font-size:${fs}px;border-left:1px solid #475569}
td{padding:8px 7px;border-bottom:1.5px solid #cbd5e1;border-left:1px solid #e2e8f0;vertical-align:middle;text-align:center;line-height:1.7}
.row-even{background:#fff}.row-odd{background:#f1f5f9}
td.r{text-align:right}
.ln{display:block}.ln b{font-weight:800}
.lb{color:#64748b;font-size:${fs - 1}px;margin-left:4px}
.nm{direction:ltr;display:inline-block;font-variant-numeric:tabular-nums}
.tot th{background:#334155}
.tot td{background:#fef3c7;font-weight:800;border-bottom:1.5px solid #d4a017}
.tot td.cur{background:#1e293b;color:#fff}
.avgline{margin-top:8px;padding:9px 14px;background:#e0f2fe;border-radius:8px;font-weight:700;font-size:${fs}px}
.footer{margin-top:22px;display:flex;justify-content:flex-end}
.fs{font-size:13px;color:#475569;border-top:1.5px solid #94a3b8;padding-top:7px;min-width:210px;text-align:center}
.sec{page-break-before:always}
@media print{thead{display:table-header-group}tr{page-break-inside:avoid}}`;
}

const fmtVal = (c: RCol | undefined, v: any): string => {
  if (v === null || v === undefined || v === "") return "";
  switch (c?.kind) {
    case "date": return jal(v);
    case "money": return nf2(num(v));
    case "qty": return nf(num(v), 4);
    case "rate": return nf(num(v), 6);
    case "int": return nf(num(v), 0);
    case "link": return `<a href="${esc(fileUrl(String(v)))}" target="_blank" style="color:#2563eb;font-weight:800">📎 ضمیمه</a>`;
    default: return esc(v);
  }
};

export const fontSizeFor = (_n: number) => 12;

// چیدمان ساده‌ی PDF: هر ستون چند خط دارد تا جدول کم‌ستون، درشت و خوانا باشد
interface PPart { key: string; label?: string; suffixKey?: string; suffix?: string; bold?: boolean; color?: string }
interface PCol { h: string; w: number; parts: PPart[]; right?: boolean }
const P = (key: string, o: Partial<PPart> = {}): PPart => ({ key, ...o });
const DT: PCol = { h: "شماره سند / تاریخ", w: 11, parts: [P("serial", { bold: true }), P("date")] };
const NOTE: PCol = { h: "توضیحات", w: 16, right: true, parts: [P("notes"), P("att")] };

function pdfLayout(spec: RSpec): PCol[] {
  const t = spec.type;
  if (t === "purchases" || t === "sales") {
    const isP = t === "purchases";
    return [
      DT,
      { h: isP ? "فروشنده" : "مشتری", w: 15, right: true, parts: [P("party", { bold: true }), P("kind")] },
      { h: "جنس", w: 17, right: true, parts: [P("item", { bold: true }), P("code", { label: "کود:" }), ...(isP ? [] : [P("warehouse", { label: "گدام:" })])] },
      { h: "تعداد", w: 11, parts: [P("qty", { suffixKey: "unit", bold: true }), P("base_qty", { label: "پایه:", suffixKey: "base_unit" })] },
      { h: "قیمت واحد", w: 10, parts: [P("price", { suffixKey: "cur" })] },
      { h: isP ? "مبلغ خرید" : "مبلغ فروش", w: 11, parts: [P("line", { suffixKey: "cur", bold: true })] },
      { h: "تخفیف / خالص", w: 12, parts: [P("discount", { label: "تخفیف:", suffixKey: "cur" }), P("net", { label: "خالص:", suffixKey: "cur", bold: true })] },
      ...(isP ? [] : [{ h: "موتر / بارنامه", w: 11, parts: [P("truck", { label: "موتر:" }), P("waybill", { label: "بارنامه:" })] } as PCol]),
      NOTE,
    ];
  }
  if (t === "payments_in" || t === "payments_out") {
    const isIn = t === "payments_in";
    return [
      DT,
      { h: isIn ? "پرداخت‌کننده" : "دریافت‌کننده", w: 18, right: true, parts: [P("party", { bold: true }), P("party_kind")] },
      { h: isIn ? "مبلغ دریافتی" : "مبلغ پرداختی", w: 15, parts: [P("amt_paid", { suffixKey: "cur_paid", bold: true })] },
      { h: "نرخ تبدیل", w: 9, parts: [P("rate")] },
      { h: "به ارز طرف حساب", w: 15, parts: [P("amt_party", { suffixKey: "cur_party", bold: true })] },
      { h: isIn ? "حساب دریافت" : "حساب پرداخت", w: 16, right: true, parts: [P("account"), P("ref", { label: "مرجع:" })] },
      NOTE,
    ];
  }
  if (t === "expenses") {
    return [
      DT,
      { h: "دسته‌بندی", w: 18, right: true, parts: [P("category", { bold: true }), P("recipient")] },
      { h: "مبلغ مصرف", w: 15, parts: [P("amount", { suffixKey: "cur", bold: true })] },
      { h: "نرخ به دالر", w: 10, parts: [P("rate")] },
      { h: "معادل دالر", w: 13, parts: [P("usd", { suffix: "USD", bold: true })] },
      { h: "حساب پرداخت‌کننده", w: 16, right: true, parts: [P("account")] },
      NOTE,
    ];
  }
  if (t === "cogs") {
    return [
      DT,
      { h: "دسته‌بندی / جنس", w: 15, right: true, parts: [P("category", { bold: true }), P("title"), P("item"), P("code", { label: "کود:" })] },
      { h: "موترها", w: 15, right: true, parts: [P("truck_count", { label: "تعداد:" }), P("trucks", { label: "موتر:" }), P("waybills", { label: "بارنامه:" }), P("inumbers", { label: "آی نمبر:" })] },
      { h: "مسیر و کرایه", w: 15, right: true, parts: [P("from", { label: "از:" }), P("to", { label: "تا:" }), P("f_base", { label: "اصل کرایه:", suffixKey: "cur" }), P("f_stop", { label: "توقفی:", suffixKey: "cur" }), P("f_toll", { label: "تول/۵٪/۲٪:", suffixKey: "cur" }), P("f_scale", { label: "باسکول:", suffixKey: "cur" }), P("f_city", { label: "شهری:", suffixKey: "cur" }), P("f_other", { label: "سایر:", suffixKey: "cur" })] },
      { h: "مبلغ", w: 13, parts: [P("amount", { suffixKey: "cur", bold: true }), P("rate", { label: "نرخ:" }), P("usd", { label: "دالر:", suffix: "USD", bold: true })] },
      { h: "حساب پرداخت", w: 10, right: true, parts: [P("account")] },
      { h: "توضیحات", w: 17, right: true, parts: [P("notes"), P("notes2", { label: "توضیحات ۲:" }), P("att")] },
    ];
  }
  // transfers
  return [
    DT,
    { h: "از حساب (مبدا)", w: 21, right: true, parts: [P("from_acc", { bold: true }), P("from_amt", { suffixKey: "from_cur", bold: true, color: "#dc2626" })] },
    { h: "به حساب (مقصد)", w: 21, right: true, parts: [P("to_acc", { bold: true }), P("to_amt", { suffixKey: "to_cur", bold: true, color: "#059669" })] },
    { h: "نرخ تبدیل", w: 13, parts: [P("rate"), P("fx", { label: "تفاوت:", suffixKey: "fx_cur" })] },
    { h: "شماره مرجع", w: 9, parts: [P("ref")] },
    NOTE,
  ];
}

function partHtml(spec: RSpec, row: Record<string, any>, p: PPart): string {
  const col = spec.cols.find(c => c.key === p.key);
  const v = row[p.key];
  if (v === null || v === undefined || v === "") return "";
  if (p.key === "fx" && num(v) === 0) return "";
  if (p.key === "rate" && num(v) === 0) return "";
  let val = fmtVal(col, v);
  if (!val) return "";
  const suf = p.suffix || (p.suffixKey ? String(row[p.suffixKey] ?? "") : "");
  const isNum = col?.kind === "money" || col?.kind === "qty" || col?.kind === "rate" || col?.kind === "int";
  let inner = isNum ? `<span class="nm">${val}${suf ? " " + esc(suf) : ""}</span>` : (suf ? `${val} ${esc(suf)}` : val);
  if (p.key === "date") inner = jal(v);
  const st = p.color ? `color:${p.color};` : p.key === "serial" ? `color:#${spec.color};` : "";
  if (p.bold || p.key === "serial") inner = `<b style="${st}">${inner}</b>`;
  else if (st) inner = `<span style="${st}">${inner}</span>`;
  return `<span class="ln">${p.label ? `<span class="lb">${esc(p.label)}</span>` : ""}${inner}</span>`;
}

function totalsHtml(spec: RSpec): string {
  if (!spec.rows.length) return "";
  const sumCols = spec.cols.filter(c => c.sum && c.showSum !== false);
  const avgCols = spec.cols.filter(c => c.sum && c.showAvg);
  const curs = currenciesOf(spec);
  const blocks: (string | null)[] = [];
  if (hasPlainSum(spec)) blocks.push(null);
  curs.forEach(c => blocks.push(c));
  const used = sumCols.filter(c => blocks.some(b => stat(spec, c, b)));
  let out = "";
  if (used.length) {
    const head = `<th>ارز</th>` + used.map(c => `<th>${esc(c.header)}</th>`).join("");
    const body = blocks.map(b => {
      if (b === null && !used.some(c => stat(spec, c, null))) return "";
      const tds = used.map(c => { const s = stat(spec, c, b); return `<td>${s ? `<span class="nm">${c.kind === "money" ? nf2(s.sum) : nf(s.sum, 4)}</span>` : ""}</td>`; }).join("");
      return `<tr><td class="cur">${b ? esc(b) : "عدد"}</td>${tds}</tr>`;
    }).join("");
    out += `<div class="tt">🧮 جمع کل</div><div class="tw"><table class="tot"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
  }
  const avgs: string[] = [];
  avgCols.forEach(c => curs.forEach(cu => { const s = stat(spec, c, cu); if (s) avgs.push(`${esc(c.header)} (میانگین) · ${esc(cu)}: <span class="nm">${nf2(s.avg)}</span>`); }));
  if (avgs.length) out += `<div class="avgline">${avgs.join("&nbsp;&nbsp;|&nbsp;&nbsp;")}</div>`;
  return out;
}

export function specHtml(spec: RSpec, withHeading = true): string {
  const cards = [{ label: "🔢 تعداد سند", value: String(spec.docCount ?? spec.rows.length) }, ...(spec.extraCards || []).map(c => ({ label: c.label, value: nf(c.value, 0) })), ...cardValues(spec).map(c => ({ label: c.label, value: nf(c.value, 2) }))];
  const cardsHtml = cards.map((c, i) => `<div class="card" style="--c:#${CARD_COLORS[i % CARD_COLORS.length]}"><div class="cl">${esc(c.label)}</div><div class="cv">${esc(c.value)}</div></div>`).join("");
  const layout = pdfLayout(spec);
  const total = layout.reduce((s, c) => s + c.w, 0);
  const head = layout.map(c => `<th style="width:${(c.w / total * 100).toFixed(1)}%">${esc(c.h)}</th>`).join("");
  let lastG = -1, shade = false;
  const body = spec.rows.length === 0
    ? `<tr><td colspan="${layout.length}" style="padding:40px;color:#64748b;font-size:16px">هیچ ردیفی یافت نشد</td></tr>`
    : spec.rows.map((r, i) => {
        if (r._g !== undefined) { if (r._g !== lastG) { shade = !shade; lastG = r._g; } } else shade = i % 2 === 0;
        const tds = layout.map((c, ci) => {
          let parts = c.parts.map(p => partHtml(spec, r, p)).filter(Boolean).join("") || "—";
          if (ci === 0 && r.status) parts += `<span class="ln" style="font-weight:800;color:${String(r.status).startsWith("در انتظار") ? "#d97706" : "#059669"}">${esc(r.status)}</span>`;
          return `<td class="${c.right ? "r" : ""}">${parts}</td>`;
        }).join("");
        return `<tr class="${shade ? "row-even" : "row-odd"}">${tds}</tr>`;
      }).join("");
  const extra = (spec.extra || []).map(ex => {
    const h = ex.cols.map(c => `<th>${esc(c.header)}</th>`).join("");
    const b = ex.rows.length ? ex.rows.map((r, i) => `<tr class="${i % 2 ? "row-odd" : "row-even"}">${ex.cols.map(c => `<td>${c.kind === "money" ? `<span class="nm">${nf2(num(r[c.key]))}</span>` : esc(r[c.key])}</td>`).join("")}</tr>`).join("")
      : `<tr><td colspan="${ex.cols.length}" style="padding:16px;color:#64748b">موردی ثبت نشده</td></tr>`;
    return `<div class="tt">${esc(ex.title)}</div><div class="tw" style="max-width:760px"><table><thead><tr>${h}</tr></thead><tbody>${b}</tbody></table></div>`;
  }).join("");
  return `${withHeading ? `<div class="tt">${esc(spec.icon)} ${esc(spec.title)}</div>` : ""}
<div class="cards">${cardsHtml}</div>
<div class="tw"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>${totalsHtml(spec)}${extra}`;
}

export function openPdfWindow(title: string, subtitle: string, icon: string, color: string, meta: ReportMeta, bodyHtml: string, fs = 12) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="fa"><head><meta charset="UTF-8"><title>${esc(title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;600;700;800&display=swap" rel="stylesheet">
<style>${pdfStyles(color, fs)}</style></head><body><div class="page">
<div class="header">
  <div class="hr"><div class="logo">${esc(icon)}</div><div><div class="ht">${esc(title)}</div><div class="hs">${esc(subtitle)}</div></div></div>
  <div class="hl"><div><span class="badge">دوره: ${esc(meta.periodLabel)}</span></div>
  <div class="pd">تاریخ چاپ: ${nowJalali()} — ${nowTime()}</div>
  ${meta.filtersLabel ? `<div class="pd" style="margin-top:2px">🔎 ${esc(meta.filtersLabel)}</div>` : ""}</div>
</div>
${bodyHtml}
<div class="footer"><div class="fs">امضا و مهر مسئول مالی</div></div>
</div></body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 800);
  return true;
}

export function printSpecPdf(spec: RSpec, meta: ReportMeta) {
  return openPdfWindow(spec.title, spec.subtitle, spec.icon, spec.color, meta, specHtml(spec, false), fontSizeFor(spec.cols.length));
}
